/* @flow */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { observable, toJS } from 'mobx';

import moment from 'moment';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Tab from '@material-ui/core/Tab';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';

import PageTitle from 'components/PageTitleLegacy/PageTitle';

import CentralForm from 'components/CentralForm/CentralForm';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
import type TypeAccountStore from 'stores/AccountStore';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import Loading from 'components/Loading/Loading';
import LocationManager from 'components/Locations/LocationManager';

import { createInviteUser } from 'permissions/account';
import AccountUsers from './components/AccountUsers/AccountUsers';
import AccountDetailsTab from './components/AccountDetailsTab/AccountDetailsTab';
import { ElectroURLTabs } from 'components/ElectroURLTabs/ElectroURLTabs';
import AccountStore from 'stores/AccountStore';

type TypeProps = {|
  accountStore: TypeAccountStore,
  actionDrawerStore: TypeActionDrawerStore,
  location: Object,
  match: Object,
  navigationStore: any,
  history: any,
|} & TranslatorProps;

@translate()
@inject('navigationStore', 'accountStore', 'actionDrawerStore')
@observer
class AccountDetailsPage extends React.Component<TypeProps> {
  @observable tabValue: number = 0;
  @observable loading: boolean = false;
  @observable companyId = '';
  @observable countryId = '';
  @observable siteId = '';

  async componentDidMount() {
    const accountStore: TypeAccountStore = this.props.accountStore;
    const accountId: string = this.props.match.params.id;

    this.tabValue = this.props.location.state ? this.props.location.state.tab : 0;

    if (accountStore.accountId !== accountId) {
      await accountStore.loadDetails(accountId);
    }

    this.props.navigationStore.reset();
    this.props.navigationStore.pushPath(0, '/accounts');
    this.props.navigationStore.pushPath(1, this.props.location.pathname);
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    const accountStore: AccountStore = this.props.accountStore;
    const accountId = this.props.match.params.id;

    this.tabValue = this.props.location.state?.tab || 0;

    if (
      accountStore.details &&
      accountStore.details.details &&
      !accountStore.loadingDetails &&
      accountStore.details.details.id !== accountId
    ) {
      accountStore.loadDetails(accountId, false);
      accountStore.loadLocations();
    }

    if (prevProps.actionDrawerStore.loadParent) {
      accountStore.loadUsers(accountId);
      accountStore.loadDetails(accountId, false);
      this.props.actionDrawerStore.loadParent = false;
    }
  }

  onDetailsTabChange = (value: number) => {
    const accountStore: AccountStore = this.props.accountStore;
    accountStore.resetLocations();
    this.tabValue = value;
  };

  render() {
    const { t, accountStore } = this.props;
    const { details } = accountStore;
    const tabValue = this.tabValue;
    const activeFilters = this.activeFilters;
    const loadingDetails = accountStore.loadingDetails;
    const isFlex = tabValue !== 0 || loadingDetails;

    if (accountStore.error) {
      return (
        <CentralForm>
          <div>
            <h1>{t('general:error')}</h1>
            <p>{accountStore.errorMessage}</p>
            <Link to="/accounts">{t('general:actions.back_to_trading_account_plural')}</Link>
          </div>
        </CentralForm>
      );
    }

    if (!details || !details.details) {
      return (
        <PageWrapper>
          <Loading />
        </PageWrapper>
      );
    }

    const title: string = `${t('general:trading_account')} | ${
      details ? details.details.name : ''
    }`;

    let accountLastSyncMoment: ?moment = null;

    if (details?.details?.last_synced_at) {
      /** @see momentjs.com/docs/#/parsing/utc/ */
      accountLastSyncMoment = moment.utc(details.details.last_synced_at);
    }

    return (
      !loadingDetails && (
        <React.Fragment>
          <PageWrapper
            isFilters={activeFilters}
            isFlex={isFlex}
            accountLastSyncMoment={accountLastSyncMoment}
          >
            <PageTitle
              title={title}
              addButton={tabValue !== 2}
              permissions={createInviteUser}
              addMultipleLinks={[
                {
                  id: 1,
                  label: t('general:actions.add_new_user_by_invitation'),
                  link: `/accounts/${this.props.accountStore.details?.details?.id}/user/invite`,
                },
              ]}
            />

            <ElectroURLTabs onChange={value => this.onDetailsTabChange(value)}>
              <Tab label={t('general:trading_accounts.labels.details')} />
              <Tab label={t('general:actions.manage_user_plural')} />
              <Tab label={t('general:actions.manage_location_plural')} />
            </ElectroURLTabs>
            <React.Fragment>
              {tabValue === 0 && <AccountDetailsTab id={this.props.match.params.id} />}
              {tabValue === 1 && <AccountUsers id={this.props.match.params.id} />}
              {tabValue === 2 && (
                <LocationManager id={this.props.match.params.id} params={this.props.match.params} />
              )}
            </React.Fragment>
          </PageWrapper>
        </React.Fragment>
      )
    );
  }
}

export default AccountDetailsPage;
